import React from 'react';

import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import {formatNumber, formatTurkishLira} from "../../common/utils";
import {EmojiEventsOutlined, InfoOutlined, InstallMobileOutlined, QrCodeOutlined} from "@mui/icons-material";
import {Tooltip} from "@mui/material";
import {RiAppleLine} from "react-icons/ri";
import {FcAndroidOs} from "react-icons/fc";
import styles from "./ConversionsResultsUI.module.scss";

const ICON_SX = {width: 18, height: 18};
const formatPercent = new Intl.NumberFormat('tr-TR', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
});

// Toplam Satış onaylanmış her dönüşümün gerçek tutarı; tablo ise yalnızca ürünü
// eşleştirilebilen dönüşümleri listeler. Bu fonksiyon aradaki farkı kalemlere ayırır.
// Her dalda kalemlerin toplamı totalSales'e eşittir.
const buildBreakdown = (reports) => {
    const rec = reports.reconciliation;
    const total = Number(reports.totalSales) || 0;
    if (!rec || total <= 0) return null;

    // Karaca (offer 123): satır cirosu ödenen kalem tutarıdır, sepet payı değil.
    // Altı kalem toplamı her zaman totalSales'e eşittir (sunucu bunu doğrular).
    const hasLineRevenue = rec.exactSales !== null && rec.exactSales !== undefined;
    const hasTableSales = rec.tableSales !== null && rec.tableSales !== undefined;
    const rows = hasLineRevenue
        ? [
            {
                // katalog tahmini ayrı kutucuk olarak gösterilmiyor; tablo tutarına
                // dahil edilir ve açıklaması bilgi balonunda belirtilir
                key: "table", color: "#2E9E6B", amount: rec.tableSales,
                label: "Ürün tablosunda",
                note: "Sepet verisinden birebir okunan, doğrulanmış ve aşağıdaki ürünlere dağıtılan geçerli satış tutarı."
            },
            {
                key: "catalog", color: "#E4572E", amount: rec.catalogMissingSales,
                label: "Ürün katalogda bulunamadı",
                note: (rec.droppedProductIds > 0
                    ? formatNumber(rec.droppedProductIds) + " ürün kodu ürün kataloğunda bulunamadı. "
                    : "") + "Satış tutarı okunabilen ancak ürün adı ve görseli bilinmediği için tabloda gösterilemeyen satışlar."
            },
            {
                key: "truncated", color: "#8A93A6", amount: rec.truncatedSales,
                label: "Sepet detayı kesildi",
                note: "Sepet verisi karakter sınırı nedeniyle kesildiği için hangi ürüne ait olduğu bilinemeyen gerçek satış tutarı."
            },
            {
                key: "unparsed", color: "#E8A33D", amount: rec.unparsedSales,
                label: "Çözümlenemedi",
                note: "Sepet verisi bozuk veya eksik geldiği için ürünlere dağıtılamayan satışlar."
            },
            {
                key: "install", color: "#5B8DEF", amount: rec.installSales,
                label: "Uygulama indirmeleri",
                note: "Ürün bilgisi taşımayan uygulama indirme kayıtları."
            }
        ]
        : hasTableSales
        ? [
            {
                key: "table", color: "#2E9E6B", amount: rec.tableSales,
                label: "Tabloda görünen",
                note: "Ürünü eşleştirilen ve aşağıda listelenen satışlar"
            },
            {
                key: "catalog", color: "#E4572E", amount: rec.droppedSales,
                label: "Ürün katalogda bulunamadı",
                note: rec.droppedProductIds > 0
                    ? formatNumber(rec.droppedProductIds) + " ürün kodu ürün kataloğunda yok"
                    : "Katalogda eşleşmeyen ürün yok"
            },
            {
                key: "install", color: "#5B8DEF", amount: rec.installSales,
                label: "Uygulama indirmeleri",
                note: "Ürün bilgisi taşımayan indirme kayıtları"
            },
            {
                key: "unparsed", color: "#E8A33D", amount: rec.unparsedSales,
                label: "Ürün verisi okunamadı",
                note: "Sepet verisi bozuk veya eksik gelen satışlar"
            }
        ]
        : [
            {
                key: "matched", color: "#2E9E6B", amount: rec.matchedSales,
                label: "Ürünü eşleşen satışlar",
                note: "Ürün kodu okunabilen ve rapora giren satışlar"
            },
            {
                key: "install", color: "#5B8DEF", amount: rec.installSales,
                label: "Uygulama indirmeleri",
                note: "Ürün bilgisi taşımayan indirme kayıtları"
            },
            {
                key: "unparsed", color: "#E8A33D", amount: rec.unparsedSales,
                label: "Ürün verisi okunamadı",
                note: "Sepet verisi bozuk veya eksik gelen satışlar"
            }
        ];

    const withShare = rows.map(row => {
        const amount = Number(row.amount) || 0;
        return {...row, amount, share: amount / total};
    });
    const inTable = (hasLineRevenue || hasTableSales) ? Number(rec.tableSales) || 0 : Number(rec.matchedSales) || 0;
    return {rows: withShare, total, gap: Math.round((total - inTable) * 100) / 100};
};

const ConversionsResultsUI = ({reports}) => {
    const rec = reports.reconciliation;
    const breakdown = buildBreakdown(reports);

    const kpis = [
        {
            key: "sales",
            show: reports.totalSales !== 0,
            primary: true,
            icon: <LocalShippingOutlinedIcon sx={ICON_SX}/>,
            label: "Toplam Satış",
            value: formatTurkishLira.format(reports.totalSales),
            sub: breakdown && breakdown.gap > 0
                ? formatTurkishLira.format(breakdown.gap) + " tabloda yok"
                : null
        },
        {
            key: "quantity",
            show: reports.totalQuantity !== 0,
            icon: <QrCodeOutlined sx={ICON_SX}/>,
            label: "Satılan Ürün",
            value: formatNumber(reports.totalQuantity),
            sub: !!rec && rec.droppedQuantity > 0
                ? formatNumber(rec.droppedQuantity) + " adet tabloda yok"
                : null
        },
        {
            key: "installs",
            show: reports.appInstallations !== 0,
            icon: <InstallMobileOutlined sx={ICON_SX}/>,
            label: "Uygulama İndirme",
            value: formatNumber(reports.appInstallations)
        },
        {
            key: "ios",
            show: reports.iosPurchases !== 0,
            icon: <RiAppleLine size={16}/>,
            label: "iOS Satın Alma",
            value: formatNumber(reports.iosPurchases)
        },
        {
            key: "android",
            show: reports.androidPurchases !== 0,
            icon: <FcAndroidOs size={18}/>,
            label: "Android Satın Alma",
            value: formatNumber(reports.androidPurchases)
        },
        {
            key: "first",
            show: reports.isFirstPurchase !== 0,
            icon: <EmojiEventsOutlined sx={ICON_SX}/>,
            label: "İlk Satın Alma",
            value: formatTurkishLira.format(reports.firstPurchaseAmount),
            sub: formatNumber(reports.isFirstPurchase) + " kez"
        }
    ].filter(kpi => kpi.show);

    return (
        <div className={styles.reportSummary}>
            <div className={styles.kpiRow}>
                {kpis.map(kpi => (
                    <div key={kpi.key}
                         className={kpi.primary ? styles.kpiCard + " " + styles.kpiPrimary : styles.kpiCard}>
                        <div className={styles.kpiHead}>
                            <span className={styles.kpiIcon}>{kpi.icon}</span>
                            <span className={styles.kpiLabel}>{kpi.label}</span>
                        </div>
                        <div className={styles.kpiValue}>{kpi.value}</div>
                        {!!kpi.sub && <div className={styles.kpiSub}>{kpi.sub}</div>}
                    </div>
                ))}
            </div>

            {!!breakdown &&
                <section className={styles.breakdown}>
                    <header className={styles.breakdownHead}>
                        <h4 className={styles.breakdownTitle}>Satış Dağılımı</h4>
                        <div className={styles.breakdownTotal}>
                            <span className={styles.breakdownTotalLabel}>Toplam</span>
                            <span className={styles.breakdownTotalValue}>
                                {formatTurkishLira.format(breakdown.total)}
                            </span>
                        </div>
                    </header>

                    <div className={styles.bar}>
                        {breakdown.rows.filter(row => row.share > 0).map(row => (
                            <span key={row.key}
                                  className={styles.barSegment}
                                  style={{width: (row.share * 100) + "%", backgroundColor: row.color}}/>
                        ))}
                    </div>

                    <ul className={styles.legend}>
                        {breakdown.rows.map(row => (
                            <Tooltip key={row.key} title={row.note || ""} arrow placement="top">
                                <li className={row.amount > 0 ? styles.legendItem : styles.legendItem + " " + styles.legendZero}>
                                    <div className={styles.legendTop}>
                                        <span className={styles.dot} style={{backgroundColor: row.color}}/>
                                        <span className={styles.legendLabel}>{row.label}</span>
                                        {!!row.note && <InfoOutlined className={styles.infoIcon}/>}
                                    </div>
                                    <div className={styles.legendNums}>
                                        <span className={styles.legendAmount}>
                                            {formatTurkishLira.format(row.amount)}
                                        </span>
                                        <span className={styles.legendPercent}>
                                            {formatPercent.format(row.share)}
                                        </span>
                                    </div>
                                </li>
                            </Tooltip>
                        ))}
                    </ul>
                </section>}
        </div>
    );
};

export default ConversionsResultsUI;
